import { types, getParent, isAlive, cast } from 'mobx-state-tree';
import { readConfObject } from '@jbrowse/core/configuration';
import { assembleLocString, getSession, getContainingDisplay, getContainingView, getViewParams, makeAbortableReaction, } from '@jbrowse/core/util';
import { Region } from '@jbrowse/core/util/types/mst';
import { isRetryException, } from '@jbrowse/core/util/types';
import { getTrackAssemblyNames, getRpcSessionId, } from '@jbrowse/core/util/tracks';
import ServerSideRenderedBlockContent from '../components/ServerSideRenderedBlockContent';
// the MST state of a single server-side-rendered block in a display
const blockState = types
    .model('BlockState', {
    key: types.string,
    region: Region,
    reloadFlag: 0,
    isLeftEndOfDisplayedRegion: false,
    isRightEndOfDisplayedRegion: false,
})
    // NOTE: all this volatile stuff has to be filled in at once, so that it stays consistent
    .volatile(() => ({
    renderInProgress: undefined,
    filled: false,
    reactElement: undefined,
    features: undefined,
    layout: undefined,
    status: '',
    error: undefined,
    message: undefined,
    maxHeightReached: false,
    ReactComponent: ServerSideRenderedBlockContent,
    renderProps: undefined,
}))
    .actions(self => {
    let renderInProgress;
    return {
        doReload() {
            self.reloadFlag = self.reloadFlag + 1;
        },
        afterAttach() {
            const display = getContainingDisplay(self);
            makeAbortableReaction(self, renderBlockData, renderBlockEffect, // reaction doesn't expect async here
            {
                name: `${display.id}/${assembleLocString(self.region)} rendering`,
                delay: display.renderDelay,
                fireImmediately: true,
            }, this.setLoading, this.setRendered, this.setError);
        },
        setStatus(message) {
            self.status = message;
        },
        setLoading(abortController) {
            if (renderInProgress !== undefined) {
                if (!renderInProgress.signal.aborted) {
                    renderInProgress.abort();
                }
            }
            self.filled = false;
            self.message = undefined;
            self.reactElement = undefined;
            self.features = undefined;
            self.layout = undefined;
            self.error = undefined;
            self.maxHeightReached = false;
            self.renderProps = undefined;
            renderInProgress = abortController;
        },
        setMessage(messageText) {
            if (renderInProgress && !renderInProgress.signal.aborted) {
                renderInProgress.abort();
            }
            self.filled = false;
            self.message = messageText;
            self.reactElement = undefined;
            self.features = undefined;
            self.layout = undefined;
            self.error = undefined;
            self.maxHeightReached = false;
            self.renderProps = undefined;
            renderInProgress = undefined;
        },
        setRendered(props) {
            if (!props) {
                return;
            }
            const { reactElement, features, layout, maxHeightReached, renderProps, } = props;
            self.filled = true;
            self.message = undefined;
            self.reactElement = reactElement;
            self.features = features;
            self.layout = layout;
            self.error = undefined;
            self.maxHeightReached = maxHeightReached;
            self.renderProps = renderProps;
            renderInProgress = undefined;
        },
        setError(error) {
            console.error(error);
            if (renderInProgress && !renderInProgress.signal.aborted) {
                renderInProgress.abort();
            }
            // the rendering failed for some reason
            self.filled = false;
            self.message = undefined;
            self.reactElement = undefined;
            self.features = undefined;
            self.layout = undefined;
            self.maxHeightReached = false;
            self.error = error;
            self.renderProps = undefined;
            renderInProgress = undefined;
            if (isRetryException(error)) {
                this.reload();
            }
        },
        reload() {
            self.renderInProgress = undefined;
            self.filled = false;
            self.reactElement = undefined;
            self.features = undefined;
            self.layout = undefined;
            self.error = undefined;
            self.message = undefined;
            self.maxHeightReached = false;
            self.ReactComponent = ServerSideRenderedBlockContent;
            self.renderProps = undefined;
            getParent(self, 2).reload();
        },
        beforeDestroy() {
            if (renderInProgress && !renderInProgress.signal.aborted) {
                renderInProgress.abort();
            }
            const display = getContainingDisplay(self);
            const { rpcManager } = getSession(self);
            const { rendererType } = display;
            const { renderArgs } = renderBlockData(cast(self));
            // renderArgs can be undefined if an error occured in this block
            if (renderArgs) {
                rendererType
                    .freeResourcesInClient(rpcManager, JSON.parse(JSON.stringify(renderArgs)))
                    .catch((e) => {
                    // just console.error if it's something while it's being destroyed
                    console.warn('Error while destroying block', e);
                });
            }
        },
    };
});
export default blockState;
// calls the render worker to render the block content not using a flow for
// this, because the flow doesn't work with autorun
export function renderBlockData(self, optDisplay) {
    try {
        const display = optDisplay || getContainingDisplay(self);
        const { assemblyManager, rpcManager } = getSession(display);
        const { adapterConfig, rendererType, error, parentTrack } = display;
        const assemblyNames = getTrackAssemblyNames(parentTrack);
        const regionAsm = self.region.assemblyName;
        if (!assemblyNames.includes(regionAsm) &&
            !assemblyNames.find(name => { var _a; return (_a = assemblyManager.get(name)) === null || _a === void 0 ? void 0 : _a.hasName(regionAsm); })) {
            throw new Error(`region assembly (${regionAsm}) does not match track assemblies (${assemblyNames})`);
        }
        const renderProps = display.renderProps();
        const { config } = renderProps;
        // This line is to trigger the mobx reaction when the config changes
        // It won't trigger the reaction if it doesn't think we're accessing it
        readConfObject(config);
        const sessionId = getRpcSessionId(display);
        const layoutId = getContainingView(display).id;
        const cannotBeRenderedReason = display.regionCannotBeRendered(self.region);
        return {
            rendererType,
            rpcManager,
            renderProps,
            cannotBeRenderedReason,
            displayError: error,
            renderArgs: {
                statusCallback: (message) => {
                    if (isAlive(self)) {
                        self.setStatus(message);
                    }
                },
                assemblyName: self.region.assemblyName,
                regions: [self.region],
                adapterConfig,
                rendererType: rendererType.name,
                sessionId,
                layoutId,
                blockKey: self.key,
                reloadFlag: self.reloadFlag,
                timeout: 1000000, // 10000,
            },
        };
    }
    catch (e) {
        return { displayError: e };
    }
}
async function renderBlockEffect(props, signal, self) {
    const { rendererType, renderProps, rpcManager, renderArgs, cannotBeRenderedReason, displayError, } = props;
    if (!isAlive(self)) {
        return undefined;
    }
    if (displayError) {
        self.setError(displayError);
        return undefined;
    }
    if (cannotBeRenderedReason) {
        self.setMessage(cannotBeRenderedReason);
        return undefined;
    }
    if (renderProps.notReady) {
        return undefined;
    }
    const { reactElement, features, layout, maxHeightReached } = await rendererType.renderInClient(rpcManager, {
        ...renderArgs,
        ...renderProps,
        viewParams: getViewParams(self),
        signal,
    });
    return {
        reactElement,
        features,
        layout,
        maxHeightReached,
    };
}
//# sourceMappingURL=serverSideRenderedBlock.js.map